import { useMutation, useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { ulid } from 'ulid';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { VehicleViewModel } from '@/graphql/generated';
import { extractCheckResultError } from '@/lib/checkResult';
import { AutocompleteCreateFormProps } from './autocomplete-create-form';

import { useTranslation } from 'react-i18next';
import { VehicleForm } from '../../pages/vehicles/vehicle-form';
import {
	buildCreateVehicleInput,
	type VehicleFormSubmitData
} from '../../pages/vehicles/vehicle-input';
import {
	VEHICLE_CREATE_MUTATION,
	VEHICLE_TYPES_QUERY
} from '../../pages/vehicles/vehicles.gql';

type VehicleTypeOption = {
	vehicleTypeId: number;
	name?: string | null;
};

export function VehicleCreateForm({
	onSuccess,
	onCancel,
	initialValue
}: AutocompleteCreateFormProps<VehicleViewModel>) {
	const [submissionError, setSubmissionError] = useState<string | null>(null);
	const idempotentVehicleId = useMemo(() => ulid(), []);
	const { t } = useTranslation();

	// Use the search term as initial plate number
	const initialPlateNumber = initialValue || '';

	const {
		data: vehicleTypesData,
		loading: isVehicleTypeLoading,
		error: vehicleTypesError
	} = useQuery<{ vehicleTypes: VehicleTypeOption[] }>(VEHICLE_TYPES_QUERY);

	const vehicleTypeOptions = useMemo(
		() =>
			(vehicleTypesData?.vehicleTypes ?? [])
				.filter(type => type?.vehicleTypeId != null)
				.map(type => ({
					value: String(type.vehicleTypeId),
					label: type.name ?? `Type ${type.vehicleTypeId}`
				})),
		[vehicleTypesData]
	);

	const [createVehicle, { loading: isSubmitting }] = useMutation(
		VEHICLE_CREATE_MUTATION,
		{
			// Prevent cache interactions that might trigger refreshes
			fetchPolicy: 'no-cache'
		}
	);

	const handleSubmit = async (formData: VehicleFormSubmitData) => {
		setSubmissionError(null);

		const payload = buildCreateVehicleInput(formData, idempotentVehicleId);

		try {
			const { data } = await createVehicle({
				variables: { input: payload }
			});

			const message = extractCheckResultError(data?.createVehicle);

			if (message) {
				setSubmissionError(message);
				return;
			}

			// Skip success toast in autocomplete context to prevent navigation issues
			// The autocomplete selection will provide user feedback

			// Create a minimal VehicleViewModel object to pass back
			// Note: vehicleType and other fields will be populated when the list refreshes
			const newVehicle = {
				vehicleId: idempotentVehicleId,
				plateNumber: formData.plateNumber,
				make: formData.make,
				model: formData.model,
				color: formData.color,
				isActive: formData.isActive
			} as VehicleViewModel;

			onSuccess(newVehicle);
		} catch (error) {
			const fallbackMessage =
				error instanceof Error
					? error.message
					: 'Unexpected error while creating vehicle.';
			setSubmissionError(fallbackMessage);
		}
	};

	return (
		<div className="space-y-4">
			{vehicleTypesError && (
				<Alert variant="destructive">
					<AlertTitle>Unable to load vehicle types</AlertTitle>
					<AlertDescription>
						{vehicleTypesError.message}
					</AlertDescription>
				</Alert>
			)}

			{submissionError && (
				<Alert variant="destructive">
					<AlertTitle>Unable to create vehicle</AlertTitle>
					<AlertDescription>{submissionError}</AlertDescription>
				</Alert>
			)}

			<VehicleForm
				mode="create"
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				vehicleTypeOptions={vehicleTypeOptions}
				isVehicleTypeLoading={isVehicleTypeLoading}
				headerTitle={t('vehicles:create.title')}
				onBack={onCancel}
				initialData={initialPlateNumber ? { plateNumber: initialPlateNumber } : undefined}
			/>
		</div>
	);
}
